<!-- src/components/ui/BrandMark.vue
     Shared logo used in header + footer: the "1=AL" heart icon mark
     (orange for spiritual, purple for finance) next to the business name.
     Source files: public/images/icon-finance.png / icon-spiritual.png
     — swap those (same filenames) to update the logo everywhere at once.
     Falls back to a small drawn mark if an icon file is ever missing.
-->
<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { SITE } from '@/app-config'

const props = defineProps({
  size: { type: String, default: 'md' } // 'md' (header) | 'sm' (footer)
})

const route = useRoute()
const section = computed(() => route.meta?.section || 'finance')
const iconSrc = computed(() =>
    section.value === 'spiritual' ? '/images/icon-spiritual.png' : '/images/icon-finance.png'
)
const brandName = computed(() => (SITE[section.value] || SITE.finance).name)

const imgFailed = ref(false)
// Reset when the theme (and thus the icon file) changes, so a working
// spiritual icon isn't stuck hidden by an earlier finance-icon error.
watch(iconSrc, () => { imgFailed.value = false })
</script>

<template>
  <span class="brand-mark" :class="`brand-mark--${size}`">
    <img
        v-if="!imgFailed"
        :src="iconSrc"
        alt=""
        class="brand-icon-img"
        @error="imgFailed = true"
    />
    <svg v-else class="brand-icon" viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <linearGradient :id="`bm-g-${size}`" x1="0" x2="1" y1="1" y2="0">
          <stop offset="0" stop-color="var(--brand)" />
          <stop offset="1" stop-color="var(--accent)" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5.5" :fill="`url(#bm-g-${size})`" />
      <path d="M8 12h8M12 8v8" stroke="#fff" stroke-width="2.2" stroke-linecap="round" />
    </svg>
    <span class="brand-name">{{ brandName }}</span>
  </span>
</template>

<style scoped>
.brand-mark { display: inline-flex; align-items: center; gap: 10px; min-width: 0; }
.brand-icon-img { display: block; object-fit: contain; flex-shrink: 0; }
.brand-mark--md .brand-icon-img { height: 34px; width: 34px; }
.brand-mark--sm .brand-icon-img { height: 24px; width: 24px; }
.brand-icon { width: 18px; height: 18px; flex-shrink: 0; }
.brand-name { font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

@media (max-width: 420px) {
  .brand-mark--md .brand-icon-img { height: 28px; width: 28px; }
}
</style>
